import React, { useEffect, useCallback } from 'react'
import { signInInitialState } from './store/State'
import IApiResponse from '../../types/IApiResponse'
import IUserData from '../../types/sign-in/IUserData'
import signInReducer from './store/Reducer'
import SignInService from './Service'
import { useSnackbar } from 'notistack'
import { navigate } from '@reach/router'
import SessionManagerService from 'smart-components/session-manager/Service'
// import WithRequireAuthenticationService from 'hocs/with-require-authentication/Service'
import { ISignInProps } from './Interfaces'
import { useVolatileReducer } from 'helpers'
import { Vault } from 'lib'

const signInContext = {
  state: signInInitialState,
  handlers: {
    onEmailChange: (_email: string) => {},
    onPasswordChange: (_password: string) => {},
    onSubmit: (_entepriseId?: string, _submissionLinkCode?: string) => new Promise<void>(() => {}),
    onEmailTouched: () => {},

    toggleResetPasswordDialog: () => {},
    setResetPasswordEmail: (_email: string) => {},
    validateResetPasswordEmail: () => {},
    requestResetPassword: () => new Promise<void>(() => {}),
    resetRequestResetPasswordState: () => {}
  }
}

export type SignInContext = typeof signInContext

export const SignInContextProvider = React.createContext<SignInContext>(signInContext)

const Provider: React.FC<ISignInProps> = ({ routeId, enterpriseId, code, children }) => {
  const [state, dispatch] = useVolatileReducer(signInReducer, signInInitialState)
  const { enqueueSnackbar } = useSnackbar()

  const onSubmissionSuccess = useCallback(
    (response: IApiResponse<IUserData>) => {
      if (response.data) {
        SessionManagerService.setCurrentUser(response.data)

        // const [redirect, path] = WithRequireAuthenticationService.decideNextRoute(response.data, routeId, '/top-fives')

        // if (redirect) {
        //   navigate(`${path}${code ? '?displayAllQuestionsMustBeComplete=true' : ''}`)
        // }
      }
    },
    [routeId, code]
  )

  const onSubmissionError = useCallback(
    (error: string) => {
      enqueueSnackbar(error, {
        variant: 'warning',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      })
    },
    [enqueueSnackbar]
  )

  const onRequestResetPasswordSuccess = useCallback(() => {
    enqueueSnackbar('Instructions have been sent to your email', {
      variant: 'success',
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right'
      }
    })
  }, [enqueueSnackbar])

  const onRequestResetPasswordError = useCallback(
    (error: string) => {
      enqueueSnackbar(error, {
        variant: 'warning',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right'
        }
      })
    },
    [enqueueSnackbar]
  )

  const handlers = {
    onEmailChange: SignInService.onEmailChange(state, dispatch),
    onEmailTouched: SignInService.onEmailTouched(state, dispatch),
    onPasswordChange: SignInService.onPasswordChanged(state, dispatch),
    onSubmit: SignInService.onSubmit(state, dispatch, onSubmissionSuccess, onSubmissionError, enterpriseId, code),

    toggleResetPasswordDialog: SignInService.toggleResetPasswordDialog(state, dispatch),
    setResetPasswordEmail: SignInService.setResetPasswordEmail(state, dispatch),
    validateResetPasswordEmail: SignInService.validateResetPasswordEmail(state, dispatch),
    requestResetPassword: SignInService.requestResetPassword(
      state,
      dispatch,
      onRequestResetPasswordSuccess,
      onRequestResetPasswordError
    ),
    resetRequestResetPasswordState: SignInService.resetRequestResetPasswordState(state, dispatch)
  }

  useEffect(() => {
    if (Vault.getAccessToken()) {
      navigate('/top-fives')
    }

    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search)

      const email = urlParams.get('e')

      handlers.onEmailChange(email)
    }
  }, [])

  const contextValue = {
    ...signInContext,
    state,
    handlers
  }

  return <SignInContextProvider.Provider value={contextValue}>{children}</SignInContextProvider.Provider>
}

export default React.memo(Provider)
